import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import clsx from 'clsx';

import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { setCoBorrowerChangedInfoAfterProvePreFill } from 'actions/application';
import { setCoBorrower } from 'actions/coBorrower';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import Loader from 'components/Loader';
import { SearchLocationInput } from 'components/SearchLocationInput';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getBorrowerShortInfo } from 'thunks';
import { MASKED_SSN_LENGTH, BORROWER_FIRST_LAST_NAME_MAX_LENGTH, SSN_MAX_LENGTH } from 'utils/constants';
import { getStreetAddress, maskSSN, removeNonDigits } from 'utils/stringUtils';
import { getMessageForInvalidFields } from 'utils/validationUtils';
import {
  validateFirstName,
  validateLastName,
} from 'pages/PreQualification/steps/BorrowerInformation/borrowerInformation.utils';
import { EyeIcon } from 'components/Icons';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import BorrowerApi, { BorrowerDto } from 'api/BorrowerApi';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';
import { applicationApi } from 'api/ApplicationApi';

import styles from './CoBorrowerInformation.module.scss';

const CoBorrowerInformation = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);

  const {
    application,
    borrower: { streetName, streetNumber, city, zip, state, personalIdNumber: borrowerSSN, county },
    coBorrower: { firstName, lastName, personalIdNumber, phone, dateOfBirth, email },
  } = useAppSelector((store) => store);

  const { register, setValue, trigger, watch, control, formState, getValues, clearErrors } = useForm({
    defaultValues: {
      [LoanInformationVariables.CoBorrowerFirstName]: firstName,
      [LoanInformationVariables.CoBorrowerLastName]: lastName,
      [LoanInformationVariables.CoBorrowerSSN]: personalIdNumber,
      [LoanInformationVariables.HomeStreetAddress]: getStreetAddress(streetNumber, streetName),
      [LoanInformationVariables.City]: city,
      [LoanInformationVariables.State]: state,
      [LoanInformationVariables.ZipCode]: zip,
      [LoanInformationVariables.County]: county || '',
    },
  });
  const watcher = watch();
  const [isSsnVisible, setSsnVisible] = useState(!application![LoanInformationVariables.IsCoBorrowerManualFlow]);

  const getErrorMessageForRequiredFields = (requiredField: string) => `${requiredField} is required`;

  const handleInputChange = () => {
    dispatch(setCoBorrowerChangedInfoAfterProvePreFill(true));
  };

  const checkMatchWithBorrowerSSN = (value: string) => {
    return value !== borrowerSSN || 'Must be different from Borrower SSN';
  };

  useEffect(() => {
    register(LoanInformationVariables.CoBorrowerFirstName, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerFirstName),
      validate: (value) => {
        return validateFirstName(value, LoanInformationVariables.CoBorrowerFirstName);
      },
    });
    register(LoanInformationVariables.CoBorrowerLastName, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerLastName),
      validate: (value) => {
        return validateLastName(value, LoanInformationVariables.CoBorrowerLastName);
      },
    });
    register(LoanInformationVariables.CoBorrowerSSN, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.CoBorrowerSSN),
      validate: (value) =>
        value.length === SSN_MAX_LENGTH
          ? checkMatchWithBorrowerSSN(value)
          : getMessageForInvalidFields(LoanInformationVariables.SSN),
    });
  }, [register, watcher]);

  useEffect(() => {
    if (!!application![LoanInformationVariables.IsCoBorrowerManualFlow]) {
      dispatch(
        setCoBorrower({
          eligible: false,
        }),
      );
    }
  }, []);

  const goToNextStep = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerIncome));
  };

  const handleSubmit = async () => {
    const result = await trigger();

    if (result) {
      dispatch(
        setCoBorrower({
          firstName: watcher[LoanInformationVariables.CoBorrowerFirstName].trim(),
          lastName: watcher[LoanInformationVariables.CoBorrowerLastName].trim(),
          personalIdNumber: watcher[LoanInformationVariables.CoBorrowerSSN],
        }),
      );

      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      try {
        const coBorrower = await dispatch(
          getBorrowerShortInfo({
            ssn: watcher[LoanInformationVariables.CoBorrowerSSN],
          }),
        ).unwrap();

        if (coBorrower) {
          const { hasApplication } = await applicationApi.checkHasBorrowerActiveApplications(coBorrower.id as string);

          if (hasApplication || coBorrower.locked) {
            dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerAlreadyHaveActiveApplication));
          } else {
            dispatch(setCoBorrower({ borrowerId: coBorrower.id }));
            goToNextStep();
          }
        } else {
          const borrowerApi = new BorrowerApi();

          const newCoBorrower = await borrowerApi.createBorrower({
            firstName: watcher[LoanInformationVariables.CoBorrowerFirstName],
            lastName: watcher[LoanInformationVariables.CoBorrowerLastName],
            personalIdNumber: watcher[LoanInformationVariables.CoBorrowerSSN],
            phone: phone,
            email: email,
            address: {
              streetName: streetName,
              streetNumber: streetNumber!,
              city: city,
              stateOrProvince: state,
              zipOrPostalCode: zip,
              county: county,
            },
            dateOfBirth: dateOfBirth,
          });

          if ((newCoBorrower as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
            notificationManager.notifyFailure(
              FailureNotificationType.UnknownError,
              'The borrower with this email already exists',
            );

            return;
          }

          const borrower = newCoBorrower as BorrowerDto;

          dispatch(setCoBorrower({ borrowerId: borrower.id as string }));
          goToNextStep();
        }
      } catch (error: any) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  };

  const handleBack = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <FormHeader title="Co-Borrower Information" percent={66} />
          <FormBody>
            <div className={styles.rowContainer}>
              <Input
                label={LoanInformationVariables.CoBorrowerFirstName}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerFirstName]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerFirstName}
                onChange={(event) => {
                  handleInputChange();
                  setValue(LoanInformationVariables.CoBorrowerFirstName, event.target.value);
                  trigger(LoanInformationVariables.CoBorrowerFirstName);
                }}
                value={watcher[LoanInformationVariables.CoBorrowerFirstName]}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
              />
              <Input
                label={LoanInformationVariables.CoBorrowerLastName}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerLastName]?.message}
                className={styles.formInput}
                name={LoanInformationVariables.CoBorrowerLastName}
                onChange={(event) => {
                  handleInputChange();
                  setValue(LoanInformationVariables.CoBorrowerLastName, event.target.value);
                  trigger(LoanInformationVariables.CoBorrowerLastName);
                }}
                value={watcher[LoanInformationVariables.CoBorrowerLastName]}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
              />
              <Input
                label={LoanInformationVariables.CoBorrowerSSN}
                className={clsx(styles.formInput, styles.ssnInput)}
                name={LoanInformationVariables.CoBorrowerSSN}
                type={isSsnVisible ? 'text' : 'password'}
                onChange={(event) => {
                  handleInputChange();

                  const value = removeNonDigits(event.target.value.trim());

                  setValue(LoanInformationVariables.CoBorrowerSSN, value);

                  clearErrors(LoanInformationVariables.CoBorrowerSSN);

                  if (formState.isSubmitted) {
                    trigger(LoanInformationVariables.CoBorrowerSSN);
                  }
                }}
                errorMessage={formState.errors[LoanInformationVariables.CoBorrowerSSN]?.message}
                value={
                  application![LoanInformationVariables.IsCoBorrowerManualFlow]
                    ? maskSSN(watcher[LoanInformationVariables.CoBorrowerSSN], !isSsnVisible)
                    : `***-**-${watcher[LoanInformationVariables.CoBorrowerSSN].substr(5)}`
                }
                disabled={!application![LoanInformationVariables.IsCoBorrowerManualFlow]}
                maxLength={isSsnVisible ? MASKED_SSN_LENGTH : SSN_MAX_LENGTH}
                inputMode="numeric"
                endAdornment={() =>
                  application![LoanInformationVariables.IsCoBorrowerManualFlow] ? (
                    <EyeIcon
                      open={isSsnVisible}
                      className={styles.eyeIcon}
                      onClick={() => {
                        setSsnVisible(!isSsnVisible);
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            </div>
            <SearchLocationInput
              control={control}
              disabled
              useFormControl
              streetLabel={LoanInformationVariables.HomeStreetAddress}
              initialValue={{
                streetAddress: watcher[LoanInformationVariables.HomeStreetAddress],
                city: watcher[LoanInformationVariables.City],
                state: watcher[LoanInformationVariables.State],
                zip: watcher[LoanInformationVariables.ZipCode],
              }}
              getValues={getValues}
            />
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleSubmit} withArrow>
              Next
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default CoBorrowerInformation;
